import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, AdminApiError } from '../../api/admin.ts';
import { Alert, Button, LoadingBlock } from '../ui.tsx';
import type { AdminCompanyUser } from '@shared/types/admin.ts';

/**
 * The roster of one company, with a "Sign in as" button on every active
 * member — the second half of the impersonation flow (the first is choosing
 * the company, which the page around this owns).
 *
 * The button navigates hard rather than routing client-side: the response's
 * real payload is the tenant session cookie, and the app has to boot against
 * it from scratch — its session query, its i18n language, its draft store and
 * every cached list belong to whoever was signed in a moment ago.
 */

function formatDateTime(value: string | null): string {
  if (!value) return 'Never';
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleString() : 'Never';
}

function roleLabel(role: string): string {
  return role.replace(/_/g, ' ');
}

export default function ImpersonateUsers({ companyId, companyName }: { companyId: string; companyName: string }) {
  const users = useQuery({
    queryKey: ['admin', 'company', companyId, 'users'],
    queryFn: ({ signal }) => adminApi.listCompanyUsers(companyId, signal),
    enabled: companyId !== '',
  });

  // Held past the navigation on purpose: the button must not spring back to
  // its idle label while the browser is already leaving the page.
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function signInAs(user: AdminCompanyUser) {
    const confirmed = window.confirm(
      `Sign in as ${user.fullName || user.email} at ${companyName}?\n\n` +
        'You will be using their account with their permissions. This is recorded in the ' +
        'admin trail only; the client does not see it.',
    );
    if (!confirmed) return;

    setBusyUserId(user.id);
    setError(null);
    try {
      const result = await adminApi.impersonate(companyId, user.id);
      window.location.assign(result.redirectTo);
    } catch (caught) {
      setError(
        caught instanceof AdminApiError ? caught.message : 'That sign-in could not be started. Please try again.',
      );
      setBusyUserId(null);
    }
  }

  if (users.isLoading) return <LoadingBlock label="Loading users…" />;
  if (users.isError || !users.data) {
    return (
      <Alert tone="error">
        That company&apos;s users could not be loaded.{' '}
        <Button variant="ghost" onClick={() => void users.refetch()}>
          Try again
        </Button>
      </Alert>
    );
  }
  if (users.data.length === 0) {
    return <Alert tone="warning">This company has no users to sign in as.</Alert>;
  }

  return (
    <div>
      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      <ul className="divide-y divide-[color:var(--color-line)]">
        {users.data.map((user) => (
          <li key={user.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="font-medium">
                {user.fullName || user.email}
                <span className="ml-2 text-xs font-normal text-[color:var(--color-muted)]">{roleLabel(user.role)}</span>
                {!user.isActive ? (
                  <span className="badge ml-2 border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                    Deactivated
                  </span>
                ) : null}
              </p>
              <p className="truncate text-xs text-[color:var(--color-muted)]">
                {user.email} · last signed in {formatDateTime(user.lastLoginAt)}
              </p>
            </div>
            <Button
              busy={busyUserId === user.id}
              // A deactivated account has no session to borrow — the API
              // refuses it too, this just says so before the click.
              disabled={!user.isActive || busyUserId !== null}
              onClick={() => void signInAs(user)}
            >
              Sign in as
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
